/**
 * @file operators.api.js
 * @module OperatorsAPI
 * @description HTTP API wrapper for operators related endpoints. Methods return Axios promises.
 */

import { axiosInstance } from "./axios";

export const OperatorsAPI = {

    /**
     * Retrieve operators with pagination and optional query.
     * @param {number} limit - Items per page.
     * @param {number} page - Page number.
     * @param {string} query - Optional query string.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to operators list.
     */
    getOperators: (limit, page, query, token = "") => {
        return axiosInstance(token).get(`/operators?limit=${limit}&page=${page}${query ? `&${query}` : ""}`);
    },

    /**
     * Create a new operator.
     * @param {Object} body - Operator payload.
     * @param {string} token - Optional auth token.
     * @returns {Promise} Axios promise resolving to created operator.
     */
    createOperator: (body, token = "") => {
        return axiosInstance(token).post(`/operators`, body);
    },

    /**
     * Get an operator by id.
     * @param {string} operatorId
     * @param {string} token
     * @returns {Promise} Axios promise resolving to operator data.
     */
    getOperatorById: (operatorId, token = "") => {
        return axiosInstance(token).get(`/operators/${operatorId}`);
    },

    /**
     * Update an operator by id.
     * @param {string} operatorId
     * @param {Object} body
     * @param {string} token
     * @returns {Promise} Axios promise resolving to updated operator.
     */
    updateOperatorById: (operatorId, body, token = "") => {
        return axiosInstance(token).put(`/operators/${operatorId}`, body);
    },

    /**
     * Delete an operator by id.
     * @param {string} operatorId
     * @param {string} token
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteOperatorById: (operatorId, token = "") => {
        return axiosInstance(token).delete(`/operators/${operatorId}`);
    },

    /**
     * Get operators options (lightweight list).
     * @param {string} token
     * @returns {Promise} Axios promise resolving to options.
     */
    getOperatorsOptions: (token = "") => {
        return axiosInstance(token).get(`/operators-options`);
    },

    /**
     * Delete many operators by payload.
     * @param {Object} body
     * @param {string} token
     * @returns {Promise} Axios promise resolving to deletion result.
     */
    deleteManyOperatorsById: (body, token = "") => {
        return axiosInstance(token).post(`/bulk-delete-operators`, body)
    },

    /**
     * Patch attributes of an operator.
     * @param {string} operatorId
     * @param {Object} operator
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to updated operator.
     */
    updateAnyAttributeById: (operatorId, operator, token = "") => {
        return axiosInstance(token).patch(`/operators/${operatorId}`, operator);
    },

    /**
     * Retrieve reservations metrics with payload.
     * @param {Object} payload
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to metrics.
     */
    getReservationsMetrics: (payload, token = "") => {
        return axiosInstance(token).post(`/reservations-metrics`, payload)
    },

    /**
     * Send confirmation email to an operator.
     * @param {string} operatorId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendEmailToConfirmAccountById: (operatorId, token = "") => {
        return axiosInstance(token).post(`/operators/${operatorId}/confirm-account`);
    },

    /**
     * Send password recovery email to an operator.
     * @param {string} operatorId
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to send result.
     */
    sendEmailToRecoverPasswordById: (operatorId, token = "") => {
        return axiosInstance(token).post(`/operators/${operatorId}/password-recovery`);
    },

    /**
     * Get operators metadata with optional query.
     * @param {string} query
     * @param {string} [token=""]
     * @returns {Promise} Axios promise resolving to metadata.
     */
    getOperatorsMetadata: (query, token = "") => {
        return axiosInstance(token).get(`/operators-metadata${query ? `?${query}` : ""}`);
    },

    /**
     * History service: get histories by operator id.
     * @param {string} operatorId
     * @param {number} limit
     * @param {number} page
     * @param {string} token
     * @returns {Promise} Axios promise resolving to histories.
     */
    getHistoriesByOperatorId: (operatorId, limit, page, token = "") => {
        return axiosInstance(token).get(`/modules/operators/${operatorId}/histories?limit=${limit}&page=${page}`)
    },

    /**
     * Update a history entry by id.
     * @param {string} historyId
     * @param {Object} history
     * @param {string} token
     * @returns {Promise} Axios promise resolving to updated history.
     */
    updateHistoryById: (historyId, history, token = "") => {
        return axiosInstance(token).put(`/histories/${historyId}`, history)
    },
}